import { useSyncExternalStore } from 'react';

import type { DataChannel } from './webrtc';

/**
 * Прямой канал PTZ к регистратору.
 *
 * Регистратор SAMVISION кладёт в оффер живого видео поворотной камеры data channel
 * `ptz` и переводит его сообщения в ONVIF камеры (NVR-Hi3536 `nvr-cloud/src/live.rs`):
 *
 *   {"t":"move","pan":…,"tilt":…,"zoom":…}  — ContinuousMove, оси −1…1
 *   {"t":"stop"}                             — Stop pan/tilt + zoom
 *
 * «Домой» по каналу нет. Ответа тоже нет: канал надёжный и упорядоченный, «стоп»
 * доходит после «едь».
 *
 * Канал живёт в сессии окна, а пульт — отдельно от окна, поэтому между ними этот
 * реестр: окно кладёт сюда открытый канал своей камеры, пульт берёт его по
 * `deviceId`.
 *
 * Каналов у одной камеры бывает НЕСКОЛЬКО, и реестр держит их все: одна и та же
 * камера открыта в окне и в полноэкранном просмотре (на телефоне это второе окно со
 * своей сессией), а при плановой смене сессии новый канал открывается раньше, чем
 * закрывается старый. Когда реестр держал по камере один канал, выход из
 * полноэкранного уносил с собой канал и окна — пульт падал в облако и «не работал».
 *
 * Событиям одним верить нельзя: `pc.close()` у своей же стороны закрывает каналы
 * БЕЗ события `close` (так в спецификации WebRTC). Поэтому живым считается только
 * канал, у которого `readyState === 'open'` прямо сейчас.
 */
export const PTZ_CHANNEL_LABEL = 'ptz';

const channels = new Map<string, DataChannel[]>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach(l => l());
}

function remove(deviceId: string, channel: DataChannel) {
  const list = channels.get(deviceId);
  if (!list) {
    return;
  }
  const rest = list.filter(c => c !== channel && c.readyState !== 'closed');
  if (rest.length) {
    channels.set(deviceId, rest);
  } else {
    channels.delete(deviceId);
  }
}

export function attachPtzChannel(deviceId: string, channel: DataChannel) {
  const put = () => {
    const list = (channels.get(deviceId) ?? []).filter(
      c => c !== channel && c.readyState !== 'closed',
    );
    channels.set(deviceId, [...list, channel]);
    emit();
  };
  channel.onopen = put;
  channel.onclose = () => {
    remove(deviceId, channel);
    emit();
  };
  if (channel.readyState === 'open') {
    put();
  }
}

/** Открытый канал камеры — самый свежий из живых — или null. */
export function ptzChannel(deviceId: string | undefined): DataChannel | null {
  const list = deviceId ? channels.get(deviceId) : undefined;
  if (!list) {
    return null;
  }
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i].readyState === 'open') {
      return list[i];
    }
  }
  return null;
}

/**
 * Сессия окна закрыта своей стороной — каналы умерли молча (см. выше). Пусть пульт
 * пересчитает, есть ли у камеры живой канал, и покажет «через облако», если нет.
 */
export function refreshPtzChannels() {
  for (const [id, list] of channels) {
    const live = list.filter(c => c.readyState !== 'closed');
    if (live.length) {
      channels.set(id, live);
    } else {
      channels.delete(id);
    }
  }
  emit();
}

/** Отправить по каналу; false — канала нет или он уже закрылся. */
export function sendPtz(
  deviceId: string,
  message: Record<string, unknown>,
): boolean {
  const ch = ptzChannel(deviceId);
  if (!ch) {
    return false;
  }
  try {
    ch.send(JSON.stringify(message));
    return true;
  } catch {
    return false;
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Камеры с открытым каналом — для просмотра, который решает, показывать ли пульт. */
let idsKey = '';
let ids: ReadonlySet<string> = new Set();
function openIds(): ReadonlySet<string> {
  const now = [...channels.keys()].filter(id => ptzChannel(id) !== null).sort();
  const key = now.join('|');
  if (key !== idsKey) {
    idsKey = key;
    ids = new Set(now);
  }
  return ids;
}

export function usePtzChannelIds(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, openIds, openIds);
}

/** Есть ли у камеры прямой канал — для пульта: показать его и выключить «домой». */
export function usePtzChannel(deviceId: string | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => ptzChannel(deviceId) !== null,
    () => false,
  );
}
